import z from 'zod';
import { v4 } from 'uuid';
import { defineMutatorWithType, type ServerTransaction } from '@rocicorp/zero';
import {
  zCreateTrackInput,
  zEditTrackInput,
  zDeleteTrackInput,
  zArchiveTrackInput,
  zMoveSubTrackInput,
  materializeTrackOrder,
  type EditTrackInput,
  type MoveSubTrackInput,
} from '@strength/domain';
import type { OrgSessionData } from '@strength/server-auth';
import { type Schema, zql } from '../zero-schema.gen';
import { zeroSchema } from '../shared/procedures';
import { type ClientInsert } from '../shared/server-defaults';

/** Create-mutator args: the shared `plan.createTrack` input plus `teamId` (carried explicitly on the Zero payload). */
export const zCreateTrackArgs = zeroSchema(zCreateTrackInput);

export type CreateTrackArgs = z.infer<typeof zCreateTrackArgs>;

// Edit carries no extra args over `zEditTrackInput` (team access is enforced
// server-side against the track's own team), so the mutator uses that schema
// directly rather than a `zeroSchema()` wrapper like create.

/** Delete-mutator args: the shared `plan.deleteTrack` input plus `teamId` (team access is enforced against this value, mirroring create). */
export const zDeleteTrackArgs = zeroSchema(zDeleteTrackInput);

export type DeleteTrackArgs = z.infer<typeof zDeleteTrackArgs>;

/** Archive/unarchive-mutator args: the shared `plan.archiveTrack` / `plan.unarchiveTrack` input plus `teamId`. Same arg shape for both directions. */
export const zArchiveTrackArgs = zeroSchema(zArchiveTrackInput);

export type ArchiveTrackArgs = z.infer<typeof zArchiveTrackArgs>;

/**
 * Track mutator context: the enriched {@link OrgSessionData} plus `serverMutations`,
 * the server-injected authoritative writes (injected, not imported, so the
 * server-only write tree never reaches the web bundle; undefined on the client).
 */
export type TrackMutatorCtx = OrgSessionData & {
  serverMutations?: {
    runCreateTrack: (
      tx: ServerTransaction<Schema, unknown>,
      args: CreateTrackArgs,
    ) => Promise<void>;
    runEditTrack: (
      tx: ServerTransaction<Schema, unknown>,
      args: EditTrackInput,
    ) => Promise<void>;
    runDeleteTrack: (
      tx: ServerTransaction<Schema, unknown>,
      args: DeleteTrackArgs,
    ) => Promise<void>;
    runArchiveTrack: (
      tx: ServerTransaction<Schema, unknown>,
      args: ArchiveTrackArgs,
    ) => Promise<void>;
    runUnarchiveTrack: (
      tx: ServerTransaction<Schema, unknown>,
      args: ArchiveTrackArgs,
    ) => Promise<void>;
    runMoveSubTrack: (
      tx: ServerTransaction<Schema, unknown>,
      args: MoveSubTrackInput,
    ) => Promise<void>;
  };
};

const defineMutator = defineMutatorWithType<Schema, TrackMutatorCtx, unknown>();

/**
 * `track` namespace mutators. Each has two branches: on the server, delegate to
 * the injected authoritative write; on the client, apply the optimistic write.
 */
export const trackMutators = {
  create: defineMutator(zCreateTrackArgs, async ({ tx, ctx, args }) => {
    if (tx.location === 'server') {
      const run = ctx.serverMutations?.runCreateTrack;
      if (!run) {
        throw new Error('track.create: server impl not injected');
      }
      await run(tx, args);
      return;
    }

    // Optimistic local write. Every server-default column is set explicitly
    // (fresh createdAt stamp, sortOrder 0, null for the nullable columns), so
    // the row matches the list query's `deletedAt IS null` filter and its
    // sorts until the authoritative row syncs back. The `satisfies` fails the
    // build if one is missing — see SERVER_DEFAULT_KEYS.
    await tx.mutate.Track.insert({
      id: args.id,
      orgId: ctx.orgId,
      createdById: ctx.profileId,
      name: args.name,
      teamId: args.teamId,
      parentTrackId: args.parentTrackId ?? null,
      color: args.color,
      createdAt: new Date().toISOString(),
      sortOrder: 0,
      updatedAt: null,
      updatedById: null,
      deletedAt: null,
      archivedAt: null,
    } satisfies ClientInsert<'Track'>);
    for (const athlete of args.athletes) {
      await tx.mutate.TrackAthlete.insert({
        id: athlete.id,
        orgId: ctx.orgId,
        createdById: ctx.profileId,
        trackId: args.id,
        profileId: athlete.profileId,
        createdAt: new Date().toISOString(),
        updatedAt: null,
        updatedById: null,
        deletedAt: null,
      } satisfies ClientInsert<'TrackAthlete'>);
    }
  }),

  edit: defineMutator(zEditTrackInput, async ({ tx, ctx, args }) => {
    if (tx.location === 'server') {
      const run = ctx.serverMutations?.runEditTrack;
      if (!run) {
        throw new Error('track.edit: server impl not injected');
      }
      await run(tx, args);
      return;
    }

    // Rename + recolor (color is always sent; null clears it).
    await tx.mutate.Track.update({
      orgId: ctx.orgId,
      id: args.id,
      name: args.name,
      color: args.color,
    });

    // Diff membership against the local store. `tx.query` is disabled for this
    // schema (enableLegacyQueries: false), so read via `tx.run(zql...)`. The
    // sync query replicates only live rows (`deletedAt IS null`), so a re-add
    // inserts a fresh optimistic row and the server authoritatively restores
    // the original soft-deleted row (Zero discards the optimistic row on
    // reconciliation); the restore branch below is a defensive no-op.
    const existing = await tx.run(
      zql.TrackAthlete.where('orgId', ctx.orgId).where('trackId', args.id),
    );

    const targetProfileIds = new Set(args.athleteIds);

    for (const profileId of args.athleteIds) {
      const hasRow = existing.some((row) => row.profileId === profileId);
      if (!hasRow) {
        await tx.mutate.TrackAthlete.insert({
          id: v4(),
          orgId: ctx.orgId,
          createdById: ctx.profileId,
          trackId: args.id,
          profileId,
          deletedAt: null,
        });
      }
    }

    for (const row of existing) {
      const isTargetTrackAthlete = targetProfileIds.has(row.profileId);
      const isDeleted = row.deletedAt !== null;

      // Removed → soft-delete. Native Date, not Luxon: Zero types deletedAt as a
      // plain ISO string (isoTimestamp customType), matching the server writes.
      if (!isTargetTrackAthlete && !isDeleted) {
        await tx.mutate.TrackAthlete.update({
          orgId: ctx.orgId,
          id: row.id,
          deletedAt: new Date().toISOString(),
        });
        continue;
      }

      // Re-add a soft-deleted row → restore.
      if (isTargetTrackAthlete && isDeleted) {
        await tx.mutate.TrackAthlete.update({
          orgId: ctx.orgId,
          id: row.id,
          deletedAt: null,
        });
      }
    }

    const removedProfileIds = existing
      .filter(
        (row) => !targetProfileIds.has(row.profileId) && row.deletedAt === null,
      )
      .map((row) => row.profileId);
    if (removedProfileIds.length === 0) {
      return;
    }

    // Mirror server editTrack: sub-tracks keep their own membership rows, so
    // dropping an athlete from the parent must drop them from children too
    // (SC-4390) or the UI still shows them until the authoritative write
    // syncs back.
    const removed = new Set(removedProfileIds);
    const subTracks = await tx.run(
      zql.Track.where('orgId', ctx.orgId).where('parentTrackId', args.id),
    );
    for (const subTrack of subTracks) {
      const subAthletes = await tx.run(
        zql.TrackAthlete.where('orgId', ctx.orgId).where(
          'trackId',
          subTrack.id,
        ),
      );
      for (const subRow of subAthletes) {
        if (removed.has(subRow.profileId) && subRow.deletedAt === null) {
          await tx.mutate.TrackAthlete.update({
            orgId: ctx.orgId,
            id: subRow.id,
            deletedAt: new Date().toISOString(),
          });
        }
      }
    }
  }),

  delete: defineMutator(zDeleteTrackArgs, async ({ tx, ctx, args }) => {
    if (tx.location === 'server') {
      const run = ctx.serverMutations?.runDeleteTrack;
      if (!run) {
        throw new Error('track.delete: server impl not injected');
      }
      await run(tx, args);
      return;
    }

    // Optimistic soft-delete; drops the row from the `deletedAt IS null` list
    // query. Block/workout side effects (parent-block soft-delete, transfer
    // clearing) are server-only, not replicated optimistically.
    await tx.mutate.Track.update({
      orgId: ctx.orgId,
      id: args.trackId,
      deletedAt: new Date().toISOString(),
    });
  }),

  archive: defineMutator(zArchiveTrackArgs, async ({ tx, ctx, args }) => {
    if (tx.location === 'server') {
      const run = ctx.serverMutations?.runArchiveTrack;
      if (!run) {
        throw new Error('track.archive: server impl not injected');
      }
      await run(tx, args);
      return;
    }

    const archivedAt = new Date().toISOString();

    // Optimistic archive; moves the row out of the active-track partition
    // (sort-tracks.ts splits on archivedAt). Cascades to sub-tracks too,
    // matching the server's setTracksArchivedAt: sort-tracks.ts drops
    // children whose parent isn't in the same active/archived partition, so
    // archiving only the parent would strand its sub-tracks off-screen until
    // the authoritative write syncs back.
    await tx.mutate.Track.update({
      orgId: ctx.orgId,
      id: args.trackId,
      archivedAt,
    });
    const subTracks = await tx.run(
      zql.Track.where('orgId', ctx.orgId).where('parentTrackId', args.trackId),
    );
    for (const subTrack of subTracks) {
      await tx.mutate.Track.update({
        orgId: ctx.orgId,
        id: subTrack.id,
        archivedAt,
      });
    }
  }),

  move: defineMutator(zMoveSubTrackInput, async ({ tx, ctx, args }) => {
    if (tx.location === 'server') {
      const run = ctx.serverMutations?.runMoveSubTrack;
      if (!run) {
        throw new Error('track.move: server impl not injected');
      }
      await run(tx, args);
      return;
    }

    // Read the moved sub-track's own athletes: they travel with it, so the
    // destination parent needs a roster row for each.
    const subTrackAthletes = await tx.run(
      zql.TrackAthlete.where('orgId', ctx.orgId).where('trackId', args.trackId),
    );

    // The server also filters these by teamId and `deletedAt IS NULL`; this
    // query relies on the replicated store already being scoped that way (the
    // track sync query is team-scoped and excludes soft-deleted rows), so the
    // two branches agree. If that sync filter ever widens, mirror it here.
    const destinationSubTracks = await tx.run(
      zql.Track.where('orgId', ctx.orgId).where(
        'parentTrackId',
        args.destinationTrackId,
      ),
    );

    const destinationRoster = await tx.run(
      zql.TrackAthlete.where('orgId', ctx.orgId).where(
        'trackId',
        args.destinationTrackId,
      ),
    );

    // The moved sub-track lands last, so the destination's children have to be
    // materialized as 1..N first: a sibling at 0 ("never reordered") sorts
    // *after* any positive value, so max + 1 alone would put the moved row
    // first. Mirrors the server's moveSubTrack.
    for (const { id, sortOrder } of materializeTrackOrder(
      destinationSubTracks,
    )) {
      await tx.mutate.Track.update({ orgId: ctx.orgId, id, sortOrder });
    }

    // Blocks need no writes — Block.trackId points at the sub-track itself.
    await tx.mutate.Track.update({
      orgId: ctx.orgId,
      id: args.trackId,
      parentTrackId: args.destinationTrackId,
      sortOrder: destinationSubTracks.length + 1,
    });

    // TrackAthlete sync replicates only live rows (deletedAt IS null), so a
    // destination row that was soft-deleted isn't visible here — we insert a
    // fresh optimistic row and the server authoritatively restores the original
    // (Zero discards the optimistic row on reconciliation). Same as `edit`.
    const destinationProfileIds = new Set(
      destinationRoster.map((row) => row.profileId),
    );
    for (const athlete of subTrackAthletes) {
      if (destinationProfileIds.has(athlete.profileId)) {
        continue;
      }
      await tx.mutate.TrackAthlete.insert({
        id: v4(),
        orgId: ctx.orgId,
        createdById: ctx.profileId,
        trackId: args.destinationTrackId,
        profileId: athlete.profileId,
        deletedAt: null,
      });
    }
  }),

  unarchive: defineMutator(zArchiveTrackArgs, async ({ tx, ctx, args }) => {
    if (tx.location === 'server') {
      const run = ctx.serverMutations?.runUnarchiveTrack;
      if (!run) {
        throw new Error('track.unarchive: server impl not injected');
      }
      await run(tx, args);
      return;
    }

    // Optimistic unarchive; moves the row back into the active-track
    // partition, cascading to sub-tracks (see archive's comment above).
    await tx.mutate.Track.update({
      orgId: ctx.orgId,
      id: args.trackId,
      archivedAt: null,
    });
    const subTracks = await tx.run(
      zql.Track.where('orgId', ctx.orgId).where('parentTrackId', args.trackId),
    );
    for (const subTrack of subTracks) {
      await tx.mutate.Track.update({
        orgId: ctx.orgId,
        id: subTrack.id,
        archivedAt: null,
      });
    }
  }),
};
